import axios from 'axios';

// API and App URLs from environment variables.
// Fall back to the current origin (not another store's domain) so a missing
// env var degrades to "this deployment talks to itself" instead of silently
// routing one store's traffic to a different store's backend.
if (!import.meta.env.VITE_API_URL || !import.meta.env.VITE_APP_URL) {
  console.error(
    '[config] VITE_API_URL / VITE_APP_URL are not set for this build. ' +
    'Falling back to window.location.origin — set these in the deployment .env file.'
  );
}
const API_URL = import.meta.env.VITE_API_URL || `${window.location.origin}/pos-backend/public/api`;
const APP_URL = import.meta.env.VITE_APP_URL || window.location.origin;

// Export for use in other components
export const getAppUrl = () => APP_URL;
export const getApiBaseUrl = () => API_URL.replace('/api', '');

// Create an Axios instance
const api = axios.create({
  baseURL: API_URL,
  // Remove the global Content-Type header - let axios handle it per request
});

// Add interceptor to include token
api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Set Content-Type to JSON only for non-FormData requests
  if (!(config.data instanceof FormData)) {
    config.headers['Content-Type'] = 'application/json';
  }
  // For FormData, let the browser set the Content-Type automatically

  return config;
}, error => Promise.reject(error));

// ========== Branches ==========
export const fetchBranches = () =>
  api.get('/branches').then(response => ({
    ...response,
    data: response.data.map(branch => ({
      id: branch.id,
      name: branch.name,
      address: branch.address,
      phone: branch.phone,
      isMain: !!branch.is_main,
    })),
  }));
export const createBranch = data => api.post('/branches', data);
export const updateBranch = (id, data) => api.put(`/branches/${id}`, data);
export const deleteBranch = id => api.delete(`/branches/${id}`);
export const setMainBranch = id =>
  api.patch(`/branches/${id}/set-main`).then(response => ({
    ...response,
    data: {
      id: response.data.id,
      name: response.data.name,
      address: response.data.address,
      phone: response.data.phone,
      isMain: !!response.data.is_main,
    },
  }));

// ========== Users =============
export const fetchUsers = () => api.get('/users');
export const createUser = data => api.post('/users', data);
export const updateUser = (id, data) => api.put(`/users/${id}`, data);
export const deleteUser = id => api.delete(`/users/${id}`);

// ========== Items =============
export const fetchItems = () => api.get('/items');
export const createItem = data => api.post('/items', data);
export const updateItem = (id, data) => api.put(`/items/${id}`, data);
export const deleteItem = id => api.delete(`/items/${id}`);

// ========== Inventory =========
export const fetchInventory = () => api.get('/inventory');
export const createInventory = data => api.post('/inventory', data);
export const updateInventory = (id, data) => api.put(`/inventory/${id}`, data);
export const deleteInventory = id => api.delete(`/inventory/${id}`);
export const transferInventory = data => api.post('/inventory/transfer', data);

// ========== Sales =============
export const fetchSales = (params = {}) => {
  const { page = 1, per_page = 10, search = '', start_date, end_date, branch_id } = params;

  const apiParams = {
    page,
    per_page
  };

  if (search && search.trim()) {
    apiParams.search = search.trim();
  }
  if (start_date) apiParams.start_date = start_date;
  if (end_date) apiParams.end_date = end_date;
  if (branch_id) apiParams.branch_id = branch_id;

  return api.get('/sales', {
    params: apiParams
  });
};

export const fetchSalesHistory = (params = {}) => {
  const { page = 1, per_page = 10, search = '', start_date, end_date, branch_id } = params;

  const apiParams = {
    page,
    per_page
  };

  if (search && search.trim()) {
    apiParams.search = search.trim();
  }
  if (start_date) apiParams.start_date = start_date;
  if (end_date) apiParams.end_date = end_date;
  if (branch_id) apiParams.branch_id = branch_id;

  return api.get('/sales-history', {
    params: apiParams
  });
};

export const fetchSale = id => api.get(`/sales/${id}`);
export const createSale = data => api.post('/sales', data);
export const updateSale = (id, data) => api.put(`/sales/${id}`, data);
export const deleteSale = id => api.delete(`/sales/${id}`);

// ===== Sale Payments (Split Payments) =====
export const fetchSalePayments = saleId => api.get(`/sales/${saleId}/payments`);
export const addSalePayment = (saleId, data) => api.post(`/sales/${saleId}/payments`, data);
export const deleteSalePayment = paymentId => api.delete(`/sale-payments/${paymentId}`);

// ======== Sale-Items =========
export const fetchSaleItems = () => api.get('/sale-items');
export const createSaleItem = data => api.post('/sale-items', data);
export const updateSaleItem = (id, data) => api.put(`/sale-items/${id}`, data);
export const deleteSaleItem = id => api.delete(`/sale-items/${id}`);

// ======== Suppliers ==========
export const fetchSuppliers = () =>
  api.get('/suppliers').then(response => ({
    ...response,
    data: response.data.map(supplier => ({
      id: supplier.id,
      name: supplier.name,
      email: supplier.email,
      phone: supplier.phone,
      address: supplier.address,
      contactPerson: supplier.contact_person,
      paymentTerm: supplier.payment_term,
      creditBalance: parseFloat(supplier.credit_balance) || 0,
    })),
  }));
export const createSupplier = data => api.post('/suppliers', data);
export const updateSupplier = (id, data) => api.put(`/suppliers/${id}`, data);
export const deleteSupplier = id => api.delete(`/suppliers/${id}`);

// Get invoices for a specific supplier
export const fetchSupplierInvoicesBySupplierId = (supplierId, status = 'pending') =>
  api.get(`/suppliers/${supplierId}/invoices`, {
    params: { status }
  }).then(response => ({
    ...response,
    data: response.data.data.map(invoice => ({
      id: invoice.id,
      invoiceNumber: invoice.invoice_number,
      date: invoice.date,
      dueDate: invoice.due_date,
      amount: parseFloat(invoice.amount),
      status: invoice.status,
      paidAmount: parseFloat(invoice.paid_amount),
      remainingAmount: parseFloat(invoice.remaining_amount)
    })),
  }));

// ==== Supplier Invoices ======
export const fetchSupplierInvoices = (supplierId, filters = {}) => {
  let url = `/supplier-invoices${supplierId ? `/${supplierId}` : ''}`;

  // Add query parameters for filters if they exist
  const params = new URLSearchParams();
  if (filters.status && filters.status !== 'All') {
    params.append('status', filters.status.toLowerCase());
  }
  if (filters.supplier_id && filters.supplier_id !== 'All') {
    params.append('supplier_id', filters.supplier_id);
  }
  if (filters.date_from) {
    params.append('date_from', filters.date_from);
  }
  if (filters.date_to) {
    params.append('date_to', filters.date_to);
  }

  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  return api.get(url).then(response => ({
    ...response,
    data: response.data.map(invoice => ({
      id: invoice.id,
      supplierId: invoice.supplier_id,
      invoiceNumber: invoice.invoice_number,
      date: invoice.date,
      dueDate: invoice.due_date,
      amount: parseFloat(invoice.amount),
      status: invoice.status,
      paidAmount: parseFloat(invoice.paid_amount),
      attachmentPath: invoice.attachment_path,
      items: invoice.items || [],
    })),
  }));
};

export const createSupplierInvoice = (formData) => {
  // FormData will automatically get the correct Content-Type header
  return api.post('/supplier-invoices', formData).catch(error => {
    throw error;
  });
};

// Create invoice with items and inventory updates
export const createSupplierInvoiceWithItems = (formData) => {
  return api.post('/supplier-invoices/with-items', formData).catch(error => {
    throw error;
  });
};

export const updateSupplierInvoice = (id, formData) => {
  // FormData will automatically get the correct Content-Type header
  return api.put(`/supplier-invoices/${id}`, formData).catch(error => {
    throw error;
  });
};

export const deleteSupplierInvoice = id =>
  api.delete(`/supplier-invoices/${id}`);

// Invoice Items Management
export const fetchInvoiceItems = invoiceId =>
  api.get(`/supplier-invoices/${invoiceId}/items`);

export const addInvoiceItem = (invoiceId, data) =>
  api.post(`/supplier-invoices/${invoiceId}/items`, data);

export const updateInvoiceItem = (invoiceId, itemId, data) =>
  api.put(`/supplier-invoices/${invoiceId}/items/${itemId}`, data);

export const deleteInvoiceItem = (invoiceId, itemId, data) =>
  api.delete(`/supplier-invoices/${invoiceId}/items/${itemId}`, { data });

// ==== Supplier Payments ======
export const fetchSupplierPayments = () => api.get('/supplier-payments');
export const fetchPaymentsByInvoice = invoiceId => api.get(`/supplier-payments/invoice/${invoiceId}`);
export const createSupplierPayment = data => api.post('/supplier-payments', data);
export const updateSupplierPayment = (id, data) => api.put(`/supplier-payments/${id}`, data);
export const deleteSupplierPayment = id => api.delete(`/supplier-payments/${id}`);

// ==== Supplier Returns ======
export const fetchSupplierReturns = supplierId =>
  api.get('/supplier-returns', { params: supplierId ? { supplier_id: supplierId } : {} });
export const fetchSupplierReturn = id => api.get(`/supplier-returns/${id}`);
export const createSupplierReturn = data => api.post('/supplier-returns', data);
export const deleteSupplierReturn = id => api.delete(`/supplier-returns/${id}`);
export const fetchSupplierReturnCatalog = supplierId => api.get(`/suppliers/${supplierId}/return-catalog`);

// ======== Settings ============
export const fetchSettings = () => api.get('/settings');
export const updateSettings = data => api.put('/settings', data);

// ========== Account Management ==========
export const fetchAccounts = () => api.get('/accounts');
export const fetchAccountSummary = () => api.get('/accounts/summary');
export const fetchAccountDetails = (id, params = {}) => api.get(`/accounts/${id}`, { params });
export const fetchAccountTransactions = (params = {}) => api.get('/accounts/transactions', { params });
export const fetchAccountBalanceReport = () => api.get('/accounts/balance-report');
export const transferBetweenAccounts = data => api.post('/accounts/transfer', data);
export const setAccountOpeningBalance = data => api.post('/accounts/opening-balance', data);
export const deleteAccountTransaction = id => api.delete(`/accounts/transactions/${id}`);

// ======== Activity Logs =======
export const fetchActivityLogs = (branchId, page = 1) =>
  api.get(`/activity-logs${branchId ? `?branch_id=${branchId}` : ''}${branchId ? '&' : '?'}page=${page}`).then(response => ({
    data: response.data.data.map(log => ({
      id: log.id,
      user_id: log.user_id,
      branch_id: log.branch_id,
      action: log.action,
      description: log.description,
      details: log.details ? JSON.parse(log.details) : null,
      created_at: log.created_at,
    })),
    pagination: response.data.pagination,
  }));

// ======== Reports ============
export const fetchProfitReport = (startDate, endDate) =>
  api.get('/reports/profit', {
    params: {
      start_date: startDate || undefined,
      end_date: endDate || undefined,
    },
  });

export const fetchProfitReportByBranch = (startDate, endDate) =>
  api.get('/reports/profit-by-branch', {
    params: {
      start_date: startDate || undefined,
      end_date: endDate || undefined,
    },
  });

export const fetchProfitStatementReport = (periodType, periods) =>
  api.get('/reports/profit-statement', {
    params: {
      period_type: periodType || undefined,
      periods: Array.isArray(periods) ? periods.join(',') : undefined,
    },
  });

export const fetchPayablesReport = (supplierId) =>
  api.get('/reports/payables', {
    params: {
      supplier_id: supplierId || undefined,
    },
  });

export const fetchSalesByBranchesReport = (startDate, endDate, branchId) =>
  api.get('/reports/sales-by-branches', {
    params: {
      start_date: startDate || undefined,
      end_date: endDate || undefined,
      branch_id: branchId || undefined,
    },
  });

export const fetchSalesByItemReport = (startDate, endDate) =>
  api.get('/reports/sales-by-items', {
    params: {
      start_date: startDate || undefined,
      end_date: endDate || undefined,
    },
  });

export const fetchDailySalesReport = (date, branchId) =>
  api.get('/reports/daily-sales', {
    params: {
      date: date || undefined,
      branch_id: branchId || undefined,
    },
  });

export const fetchStatementSalesDetails = (startDate, endDate) =>
  api.get('/reports/statement-sales-details', {
    params: {
      start_date: startDate || undefined,
      end_date: endDate || undefined,
    },
  });

export const fetchStatementLayawayDetails = (startDate, endDate) =>
  api.get('/reports/statement-layaway-details', {
    params: {
      start_date: startDate || undefined,
      end_date: endDate || undefined,
    },
  });

export const fetchStatementExpenseDetails = (startDate, endDate, categoryId) =>
  api.get('/reports/statement-expense-details', {
    params: {
      start_date: startDate || undefined,
      end_date: endDate || undefined,
      category_id: categoryId || undefined,
    },
  });

// ======== Expense Categories ========
export const fetchExpenseCategories = () => api.get('/expense-categories');
export const createExpenseCategory = data => api.post('/expense-categories', data);
export const updateExpenseCategory = (id, data) => api.put(`/expense-categories/${id}`, data);
export const deleteExpenseCategory = id => api.delete(`/expense-categories/${id}`);

// ======== Expenses ========
export const fetchExpenses = (params = {}) => {
  const { page = 1, per_page = 20, ...filters } = params;

  return api.get('/expenses', {
    params: {
      page,
      per_page,
      ...filters
    }
  });
};

export const fetchExpense = id => api.get(`/expenses/${id}`);
export const createExpense = data => api.post('/expenses', data);
export const updateExpense = (id, data) => api.put(`/expenses/${id}`, data);
export const deleteExpense = id => api.delete(`/expenses/${id}`);
export const approveExpense = (id, action, comments = null) =>
  api.post(`/expenses/${id}/approve`, { action, comments });
export const fetchExpenseStatistics = (filters = {}) =>
  api.get('/expenses/statistics', { params: filters });

// ========== Returns ==========
export const fetchReturns = (filters = {}) => {
  const { page = 1, per_page = 20, ...otherFilters } = filters;
  return api.get('/returns', {
    params: {
      page,
      per_page,
      ...otherFilters
    }
  });
};

export const fetchReturn = id => api.get(`/returns/${id}`);
export const createReturn = data => api.post('/returns', data);
export const fetchSaleReturns = saleId => api.get(`/sales/${saleId}/returns`);

// ========== Layaways ==========
export const fetchLayaways = (params = {}) => {
  const { page = 1, per_page = 10, search = '', status = '' } = params;

  const apiParams = {
    page,
    per_page
  };

  if (search && search.trim()) {
    apiParams.search = search.trim();
  }

  if (status && status.trim()) {
    apiParams.status = status.trim();
  }

  return api.get('/layaways', {
    params: apiParams
  });
};

export const fetchLayaway = id => api.get(`/layaways/${id}`);
export const createLayaway = data => api.post('/layaways', data);
export const updateLayaway = (id, data) => api.put(`/layaways/${id}`, data);
export const updateDebtorProfile = data => api.post('/layaways/customer-profile/update', data);
export const addLayawayPayment = (id, data) => api.post(`/layaways/${id}/payments`, data);
export const updateLayawayPayment = (layawayId, paymentId, data) => api.put(`/layaways/${layawayId}/payments/${paymentId}`, data);
export const fetchLayawayPayments = id => api.get(`/layaways/${id}/payments`);
export const cancelLayaway = id => api.post(`/layaways/${id}/cancel`);
export const deleteLayaway = id => api.delete(`/layaways/${id}`);
export const fetchActiveLayaways = () => api.get('/layaways/active');

api.interceptors.response.use(
  response => {
    const refreshedToken =
      response.headers['x-auth-refresh-token'] ||
      response.headers['X-Auth-Refresh-Token'];
    if (refreshedToken) {
      localStorage.setItem('token', refreshedToken);
      api.defaults.headers.common.Authorization = `Bearer ${refreshedToken}`;
    }
    return response;
  },
  error => {
    if (error.response?.status === 401) {
      const isLoginPage = window.location.pathname === '/login';
      const isUnauthorizedPage = window.location.pathname === '/unauthorized';
      const hadToken = !!localStorage.getItem('token');

      // Log the authentication error for debugging
      console.log('Authentication error (401):', {
        message: error.response?.data?.error,
        currentPath: window.location.pathname,
        redirecting: !isLoginPage && !isUnauthorizedPage,
        hadToken
      });

      if (!isLoginPage && !isUnauthorizedPage) {
        // Clear all authentication data immediately
        localStorage.clear();
        sessionStorage.clear();

        // Redirect immediately without showing popup to avoid user continuing to use the system
        window.location.replace('/login');
      }
    }
    return Promise.reject(error);
  }
);

export default api;
